import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, TextField, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('login'); // login, 2fa, setup
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const navigate = useNavigate();
  const { login, loginWithToken } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (step === 'login') {
      const result = await login(email, password);
      if (result.success) {
        navigate('/');
      } else if (result.requires_2fa) {
        setStep('2fa');
      } else if (result.requires_2fa_setup) {
        setTempToken(result.temp_token);
        // Fetch QR code
        try {
          const setupRes = await axios.post('/auth/2fa/setup', {}, {
            headers: { Authorization: `Bearer ${result.temp_token}` }
          });
          setQrCode(setupRes.data.qr_code);
          setSecret(setupRes.data.secret);
          setStep('setup');
        } catch (err) {
          setError('Failed to generate QR code');
        }
      } else {
        setError(result.error);
      }
    } else if (step === '2fa') {
      const result = await login(email, password, totpCode);
      if (result.success) {
        navigate('/');
      } else {
        setError(result.error);
        setTotpCode('');
      }
    }

    setLoading(false);
  };

  const handleVerifySetup = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.post('/auth/2fa/verify', { code: setupCode }, {
        headers: { Authorization: `Bearer ${tempToken}` }
      });
      if (res.data.success && res.data.token) {
        loginWithToken(res.data.token, res.data.user);
        navigate('/');
      } else {
        setError('Verification failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code. Try again.');
      setSetupCode('');
    }
    setLoading(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC', p: { xs: 2, sm: 3 } }}>
      <Card sx={{ maxWidth: 420, width: '100%' }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
            <img src="/logo.png" alt="Trackoo" style={{ height: 50, marginBottom: 16 }} onError={(e) => { e.target.style.display = 'none'; }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              Trackoo
            </Typography>
            <Typography variant="body2" color="text.secondary">Performance Marketing Platform</Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          {/* Step 1: Login */}
          {step === 'login' && (
            <form onSubmit={handleSubmit} autoComplete="on">
              <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} margin="normal" required autoComplete="email" name="email" id="email" />
              <TextField fullWidth label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} margin="normal" required autoComplete="current-password" name="password" id="password" />
              <Button type="submit" fullWidth variant="contained" size="large" disabled={loading} sx={{ mt: 3, py: 1.5 }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
            </form>
          )}

          {/* Step 2: Enter 2FA code */}
          {step === '2fa' && (
            <form onSubmit={handleSubmit}>
              <Alert severity="info" sx={{ mb: 2 }}>Enter the 6-digit code from your authenticator app</Alert>
              <TextField
                fullWidth
                label="2FA Code"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                margin="normal"
                required
                autoFocus
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
              />
              <Button type="submit" fullWidth variant="contained" size="large" disabled={loading || totpCode.length !== 6} sx={{ mt: 3, py: 1.5 }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
              </Button>
              <Button fullWidth onClick={() => { setStep('login'); setTotpCode(''); setError(''); }} sx={{ mt: 1 }}>Back</Button>
            </form>
          )}

          {/* Step 3: Setup 2FA */}
          {step === 'setup' && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>Two-factor authentication is required for your account. Scan the QR code below with Google Authenticator or similar app.</Alert>
              {qrCode && (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img src={qrCode} alt="QR Code" style={{ width: 200, height: 200 }} />
                </Box>
              )}
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                Manual key: {secret}
              </Typography>
              <TextField
                fullWidth
                label="Enter 6-digit code to verify"
                value={setupCode}
                onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                margin="normal"
                autoFocus
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
              />
              <Button fullWidth variant="contained" size="large" disabled={loading || setupCode.length !== 6} onClick={handleVerifySetup} sx={{ mt: 2, py: 1.5 }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Activate 2FA'}
              </Button>
            </Box>
          )}

          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 3, color: 'text.secondary' }}>
            © {new Date().getFullYear()} Digitoo Group
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Login;
